import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CCol, CRow, CButton, CAlert, CSpinner, CCard, CCardBody, CBadge,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilActionUndo, cilArrowLeft, cilPrint } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import DevolucionFacturaModal, { DevolucionCreada } from '../components/DevolucionFacturaModal'

// Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
// empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99", aticket.cs:222). [RGM] 2026-10-02 (F)

// [RGM] 2026-09-29 (pedido Rafael: "la factura no se ve como una factura,
// mejorar el diseño tipo factura"): etiquetas fieles a GX (facturaww.cs:
// 3755-3802) para el encabezado y los metadatos.
// [RGM] 2026-10-14 (devoluciones Fase 1): 5=Devolución = documento de
// devolución (Factura en negativo — ReglasFacturacion.cs:106).
const ORIGEN_FACTURA: Record<number, string> = { 1: 'POS', 2: 'App' }
// [RGM] 2026-10-14 (AJUSTES devoluciones, punto 4): 4 = «Devuelta totalmente»
// (ReglasFacturacion.EstadoFacturaDevueltaTotalmente = 4) — la devolución
// total conserva la factura en el historial con este estado, ya no Cancelada.
const ESTADO_FACTURA: Record<number, string> = {
  1: 'Abierta', 2: 'Activa', 3: 'Cancelada', 4: 'Devuelta totalmente',
}
const FPAGO_FACTURA: Record<number, string> = {
  1: 'Contado', 2: 'Credito', 3: 'Tarjeta Debito', 4: 'Tarjeta Credito', 5: 'Devolución',
}

// [RGM] 2026-10-05 (pedido Rafael: "acomoda la factura… colores de Siprinia…
// mejor formato… el PDF mostraba el navbar y todo"): vista con el azul
// institucional (#078bcd — $primary del tema, scss/style.scss:10) y bloque
// .factura-print que las reglas @media print de style.scss aíslan para el PDF
// (sin navbar/sidebar/footer ni botones).
const PRIMARY_BLUE = '#078bcd'

const FacturaViewPage: React.FC = () => {
  const { nro } = useParams<{ nro: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [factura, setFactura] = useState<any>(null)
  const [empresaNombre, setEmpresaNombre] = useState('')
  const [empresaLogo, setEmpresaLogo] = useState<string | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const isAdminFactura = ['superadmin', 'companyadmin'].includes(user?.role ?? '')
  // [RGM] 2026-10-14 (devoluciones Fase 1 — diseño aprobado): estado del modal
  // compartido + último documento creado (mensaje con "Imprimir devolución").
  const [modalDevolucion, setModalDevolucion] = useState(false)
  const [devolucionCreada, setDevolucionCreada] = useState<DevolucionCreada | null>(null)

  // [RGM] 2026-09-29: detalle con LÍNEAS (GET /facturas/{nro} — endpoint nuevo)
  // + nombre/logo de la empresa para el encabezado tipo factura. [RGM]
  // 2026-10-14 (devoluciones Fase 1): extraído a cargar() para refrescar la
  // vista tras registrar una devolución (observación nueva en la factura
  // original y estado 4 «Devuelta totalmente» cuando es devolución total —
  // AJUSTES punto 4).
  const cargar = () => {
    setCargando(true)
    setError('')
    Promise.all([
      api.get<any>(`/facturas/${nro}?empresa=${empresa}`).catch(() => null),
      api.get<any>(`/cuenta?empresa=${encodeURIComponent(empresa)}`).catch(() => null),
    ])
      .then(([f, c]) => {
        if (!f) setError('Factura no encontrada')
        setFactura(f)
        setEmpresaNombre(c?.nombre ?? '')
        setEmpresaLogo(c?.logoUrl ?? null)
      })
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [nro, empresa])

  // PDF del documento de devolución (helper global api.abrirPdf — pestaña
  // nueva, patrón anti-popup). [RGM] 2026-10-14
  const imprimirDevolucion = async (nroDocumento: number) => {
    try {
      await api.abrirPdf(`/facturas/${nroDocumento}/devolucion.pdf?empresa=${empresa}`)
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo abrir el PDF de la devolución')
    }
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (error && !factura) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Factura</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/facturas')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Factura no encontrada</CAlert>
      </>
    )
  }

  const fecha = String(factura?.facturaFecha ?? '').slice(0, 10)
  const estado = ESTADO_FACTURA[Number(factura?.facturaEstado)] ?? '—'
  const estadoColor = estado === 'Activa' ? 'success'
    : estado === 'Cancelada' ? 'danger'
    : estado === 'Devuelta totalmente' ? 'warning' : 'secondary'
  const lineas = factura?.lineas ?? []
  // Totales: mismas cuentas del form (factura_bc.cs:924-925) — Descuento =
  // SubTotal × %/100 y neto = SubTotal − Descuento (base del IVA).
  const subTotal = Number(factura?.subTotal ?? 0)
  const descuentoPct = Number(factura?.descuento ?? 0)
  const descuentoValor = subTotal * (descuentoPct / 100)
  const neto = subTotal - descuentoValor
  // [RGM] 2026-10-14 (devoluciones Fase 1 — diseño aprobado): "Devolver" SOLO
  // para admin/superadmin sobre facturas NO devolución y ni Canceladas (3) ni
  // Devueltas totalmente (4) — AJUSTES punto 4 (el backend valida lo mismo:
  // FacturasController.cs:716-739).
  const puedeDevolver = isAdminFactura && !factura?.esDevolucion
    && ![3, 4].includes(Number(factura?.facturaEstado))

  return (
    <>
      <CRow className="mb-3 no-print">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h4 className="mb-0">Factura {nro}</h4>
          <div className="d-flex gap-2">
            {puedeDevolver && (
              <CButton color="warning" variant="outline" onClick={() => setModalDevolucion(true)}>
                <CIcon icon={cilActionUndo} className="me-1" size="sm" />
                Devolución
              </CButton>
            )}
            {/* Vista SOLO LECTURA (facturaview GX): la edición/eliminación vive
                en el grid facturaww. */}
            <CButton color="primary" variant="outline" onClick={() => window.print()}>
              <CIcon icon={cilPrint} className="me-1" size="sm" />
              Imprimir
            </CButton>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/facturas')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </div>
        </CCol>
      </CRow>

      {/* [RGM] 2026-10-14 (devoluciones Fase 1): éxito del registro con la
          acción "Imprimir devolución" (PDF del documento devuelto). */}
      {devolucionCreada && (
        <CAlert color="success" className="no-print d-flex flex-wrap align-items-center justify-content-between gap-2"
          dismissible onClose={() => setDevolucionCreada(null)}>
          <span>
            Devolución #{devolucionCreada.devolucionNro} registrada — Total devuelto {fmtMoneda(devolucionCreada.totalDevuelto)}
            {devolucionCreada.devolucionTotal ? ' — factura devuelta totalmente' : ''}.
          </span>
          <CButton size="sm" color="success" variant="outline"
            onClick={() => imprimirDevolucion(devolucionCreada.devolucionNro)}>
            Imprimir devolución
          </CButton>
        </CAlert>
      )}

      {error && <CAlert color="danger" className="no-print">{error}</CAlert>}

      <div className="factura-print">
        <CCard
          className="shadow-sm factura-card"
          style={{ borderRadius: 8, maxWidth: 920, borderTop: `4px solid ${PRIMARY_BLUE}` }}
        >
          <CCardBody style={{ padding: '22px 26px' }}>
            {/* ── Encabezado tipo factura ── */}
            <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                {empresaLogo && (
                  <img
                    src={empresaLogo}
                    alt="Logo"
                    style={{ height: 52, width: 52, objectFit: 'contain', borderRadius: 8 }}
                  />
                )}
                <div>
                  <div className="fs-4 fw-bold" style={{ color: PRIMARY_BLUE }}>{empresaNombre || 'SIPRINIA'}</div>
                  <div className="text-body-secondary small">Ruta: {factura?.zonaNombre || '—'}</div>
                  <div className="text-body-secondary small">Creada por: {factura?.facturaCreadaPor || '—'}</div>
                </div>
              </div>
              <div className="text-end">
                <div className="fs-4 fw-bold" style={{ color: PRIMARY_BLUE }}>FACTURA</div>
                <div>Nro. <span className="fw-semibold">{factura?.facturaNro}</span></div>
                <div className="text-body-secondary small">{fecha} {factura?.facturaHora ?? ''}</div>
                <CBadge color={estadoColor} className="mt-1">{estado}</CBadge>
              </div>
            </div>

            {/* ── Cliente (panel organizado) [RGM] 2026-10-05 ──
                Columnas con xs (no md): en impresión A4 el ancho útil ronda
                718px (<768px del breakpoint md) y las md colapsaban a vertical
                en el PDF (reporte Rafael: "los datos del cliente en vertical").
                Con xs la grilla se mantiene igual a la vista en pantalla. */}
            <div
              className="rounded p-3 mb-3"
              style={{ background: 'rgba(7, 139, 205, 0.06)', border: '1px solid rgba(7, 139, 205, 0.25)' }}
            >
              <CRow className="g-3">
                <CCol xs={6}>
                  <div className="factura-etiqueta">Cliente</div>
                  <div className="fw-semibold">{factura?.clienteNombre || '—'}</div>
                </CCol>
                <CCol xs={3}>
                  <div className="factura-etiqueta">Cédula</div>
                  <div>{factura?.clienteCedula ?? '—'}</div>
                </CCol>
                <CCol xs={3}>
                  <div className="factura-etiqueta">Celular</div>
                  <div>{factura?.clienteCelular || '—'}</div>
                </CCol>
                <CCol xs={6}>
                  <div className="factura-etiqueta">Dirección</div>
                  <div>{factura?.clienteDireccion || '—'}</div>
                </CCol>
                <CCol xs={3}>
                  <div className="factura-etiqueta">Origen</div>
                  <div>{ORIGEN_FACTURA[Number(factura?.facturaOrigen)] ?? '—'}</div>
                </CCol>
                <CCol xs={3}>
                  <div className="factura-etiqueta">Forma de pago</div>
                  <div>{FPAGO_FACTURA[Number(factura?.facturaFPago)] ?? '—'}</div>
                </CCol>
              </CRow>
            </div>

            {/* ── Líneas (productos) ── */}
            <CTable small bordered align="middle" className="mb-3 factura-tabla">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell style={{ width: 70 }}>Nro</CTableHeaderCell>
                  <CTableHeaderCell>Producto</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 100 }}>Cantidad</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 130 }}>Valor</CTableHeaderCell>
                  <CTableHeaderCell className="text-end" style={{ width: 140 }}>Subtotal</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {lineas.map((l: any, i: number) => (
                  <CTableRow key={i}>
                    <CTableDataCell>{l.productoNro}</CTableDataCell>
                    <CTableDataCell>{l.productoNombre || '—'}</CTableDataCell>
                    <CTableDataCell className="text-end">{Number(l.cantidad ?? 0)}</CTableDataCell>
                    <CTableDataCell className="text-end">{fmtMoneda(l.valor)}</CTableDataCell>
                    <CTableDataCell className="text-end">{fmtMoneda(l.subtotal)}</CTableDataCell>
                  </CTableRow>
                ))}
                {lineas.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={5} className="text-center text-body-secondary">
                      Sin productos
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>

            {/* ── Totales (misma secuencia del form: SubTotal · Descuento ·
                SubTotal con Descuento · IVA · TOTAL — factura_bc.cs:924-925) ── */}
            <div className="d-flex justify-content-end">
              <div style={{ minWidth: 320 }}>
                <div className="d-flex justify-content-between border-bottom py-1">
                  <span className="text-body-secondary">Sub Total</span>
                  <span>{fmtMoneda(subTotal)}</span>
                </div>
                <div className="d-flex justify-content-between border-bottom py-1">
                  <span className="text-body-secondary">Descuento ({descuentoPct}%)</span>
                  {/* En una factura normal el descuento es positivo; en el
                      documento de devolución TODO va negativo (el % resuelto
                      es el de la original — AJUSTES A1), por eso el importe
                      negativo se muestra tal cual. */}
                  <span>{descuentoValor > 0 ? `− ${fmtMoneda(descuentoValor)}` : fmtMoneda(descuentoValor)}</span>
                </div>
                <div className="d-flex justify-content-between border-bottom py-1">
                  <span className="text-body-secondary">SubTotal con Descuento</span>
                  <span>{fmtMoneda(neto)}</span>
                </div>
                <div className="d-flex justify-content-between border-bottom py-1">
                  <span className="text-body-secondary">IVA</span>
                  <span>{fmtMoneda(factura?.facturaIVA)}</span>
                </div>
                <div className="d-flex justify-content-between py-2 fs-5 fw-bold" style={{ color: PRIMARY_BLUE }}>
                  <span>TOTAL</span>
                  <span>{fmtMoneda(factura?.total)}</span>
                </div>
                {Number(factura?.facturaFPago) === 2 && (
                  <>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-body-secondary">Inicial</span>
                      <span>{fmtMoneda(factura?.inicial)}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-body-secondary">A financiar</span>
                      <span>{fmtMoneda(factura?.aFinanciar)}</span>
                    </div>
                    {/* [RGM] 2026-10-07 (feature factura-credito-unificado T5
                        — papel único): el PLAN DEL CRÉDITO va EN la factura
                        (como el POS real: "Crédito: N cuotas de $X"). Datos
                        del crédito ligado (PrestamoFacturaNro). */}
                    {factura?.credito && (
                      <div
                        className="d-flex justify-content-between align-items-center py-2 mt-1 px-2 rounded"
                        style={{
                          background: 'rgba(7, 139, 205, 0.08)',
                          border: '1px dashed rgba(7, 139, 205, 0.45)',
                        }}
                      >
                        <span className="fw-semibold" style={{ color: PRIMARY_BLUE }}>
                          Crédito #{factura.credito.prestamoNro}
                        </span>
                        <span className="fw-semibold">
                          {Number(factura.credito.prestamoCuotas || 0) > 0
                            ? `${factura.credito.prestamoCuotas} cuotas de ${fmtMoneda(factura.credito.prestamoCuota)}`
                            : `Pago Libre — cuota ${fmtMoneda(factura.credito.prestamoCuota)}`}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {factura?.facturaObservcaiones && (
              <div className="mt-3 pt-3 border-top">
                <div className="factura-etiqueta">Observaciones</div>
                <div>{factura.facturaObservcaiones}</div>
              </div>
            )}
          </CCardBody>
        </CCard>
      </div>

      {/* [RGM] 2026-10-14 (devoluciones Fase 1): modal compartido con la grilla
          (DevolucionFacturaModal). Al crear: cierra, refresca el detalle —la
          factura original gana la observación de la devolución y puede quedar
          Cancelada— y deja el mensaje con "Imprimir devolución". */}
      <DevolucionFacturaModal
        facturaNro={modalDevolucion ? (Number(nro) || null) : null}
        empresa={empresa}
        onClose={() => setModalDevolucion(false)}
        onCreada={(res) => {
          setModalDevolucion(false)
          setDevolucionCreada(res)
          cargar()
        }}
      />
    </>
  )
}

export default FacturaViewPage
